import { useState, useCallback, useRef } from "react";
import { fetchPost } from "../utils/axios.tsx";
import {
  acquireAudioElement,
  playbackVolume,
  resetAudioElement,
} from "../voice/audioPlayback.ts";

type TTSState = "idle" | "loading" | "playing";

export interface SpeakOptions {
  /**
   * Fired once when playback actually finishes.
   *
   * The promise returned by `speak` settles when the audio has finished
   * *downloading*, which is earlier - fine for a listen button, but voice mode
   * would re-arm the microphone while Tally is still talking. It is not called
   * when playback is stopped by the caller (e.g. barge-in).
   */
  onEnded?: () => void;
  /**
   * Fired once when the audio starts playing - from then on it can be heard.
   * Before, the request may still be waiting on its first audio.
   */
  onStarted?: () => void;
  /**
   * Audio already requested with `requestSpeech`, played instead of fetching
   * the text again. Its fetch is aborted along with playback.
   */
  request?: SpeechRequest;
}

/** A speech request started ahead of playing it. */
export interface SpeechRequest {
  response: Promise<Response>;
  controller: AbortController;
}

/**
 * Starts synthesising `text` without playing it.
 *
 * The speech service takes about a second to send its first audio. Asking for
 * the next piece while the current one plays hides that second, instead of
 * leaving it as silence between the two. The caller must hand the request to
 * `speak`, or abort it.
 */
export function requestSpeech(
  exchangeID: string | undefined,
  text: string,
): SpeechRequest {
  const controller = new AbortController();
  const response = fetchPost(
    "/v1/text-to-speech/",
    { text, exchange_id: exchangeID },
    { signal: controller.signal },
  );
  // A request aborted before it is played is never awaited.
  response.catch(() => {});
  return { response, controller };
}

export function useTextToSpeech() {
  const [state, setState] = useState<TTSState>("idle");
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const playAttemptedRef = useRef(false);
  const isStoppedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onEndedRef = useRef<(() => void) | null>(null);
  const onStartedRef = useRef<(() => void) | null>(null);

  const stop = useCallback(() => {
    if (isStoppedRef.current) return;

    isStoppedRef.current = true;

    // A caller-initiated stop is not a natural end of playback.
    onEndedRef.current = null;
    onStartedRef.current = null;

    // Abort any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clean up audio. The element is shared and stays alive across utterances
    // so it keeps its autoplay activation - only its source is detached.
    if (audioRef.current) {
      resetAudioElement();
      audioRef.current = null;
    }

    // Clean up MediaSource
    if (mediaSourceRef.current) {
      const mediaSource = mediaSourceRef.current;
      try {
        if (mediaSource.readyState !== "closed") {
          mediaSource.endOfStream();
        }
      } catch (err) {
        // Ignore errors during cleanup
      }

      mediaSourceRef.current = null;
    }

    // Clean up SourceBuffer
    sourceBufferRef.current = null;

    // Reset flags
    playAttemptedRef.current = false;
    isStoppedRef.current = false;

    // Update state
    setState("idle");
  }, []);

  const attemptPlay = useCallback(async (audio: HTMLAudioElement) => {
    if (playAttemptedRef.current || isStoppedRef.current) return;

    playAttemptedRef.current = true;

    try {
      await audio.play();
      setState("playing");
      const notifyStarted = onStartedRef.current;
      onStartedRef.current = null;
      notifyStarted?.();
      return true;
    } catch (err) {
      console.error("Audio play failed:", err);
      return false;
    }
  }, []);

  const speak = useCallback(
    async (
      exchangeID: string | undefined,
      text: string,
      options: SpeakOptions = {},
    ) => {
      // Stop any current playback
      stop();

      // Reset stop flag
      isStoppedRef.current = false;
      onEndedRef.current = options.onEnded ?? null;
      onStartedRef.current = options.onStarted ?? null;
      // This call's own controller. `isStoppedRef` is reset as soon as a stop
      // finishes, so it cannot tell a later handler that *this* call was
      // stopped; the controller's signal can.
      const controller = options.request?.controller ?? new AbortController();
      abortControllerRef.current = controller;

      setState("loading");

      return new Promise<void>(async (resolve, reject) => {
        try {
          // Create MediaSource
          const mediaSource = new MediaSource();
          mediaSourceRef.current = mediaSource;

          // Reuse the shared, gesture-unlocked element. Creating one here would
          // be blocked by autoplay policy for every utterance voice mode speaks
          // after the first, since only entering voice mode is a user gesture.
          const audio = acquireAudioElement();
          audioRef.current = audio;
          audio.volume = playbackVolume();

          // Create object URL
          const objectUrl = URL.createObjectURL(mediaSource);
          audio.src = objectUrl;

          const onSourceOpen = async () => {
            if (isStoppedRef.current) {
              URL.revokeObjectURL(objectUrl);
              return;
            }

            try {
              // Create SourceBuffer
              const mimeType = "audio/mpeg";
              if (!MediaSource.isTypeSupported(mimeType)) {
                throw new Error(`MIME type ${mimeType} not supported`);
              }

              const sourceBuffer = mediaSource.addSourceBuffer(mimeType);
              sourceBufferRef.current = sourceBuffer;

              // Set up audio events
              audio.onended = () => {
                if (!isStoppedRef.current) {
                  const notifyEnded = onEndedRef.current;
                  stop();
                  notifyEnded?.();
                  resolve();
                }
              };

              audio.onerror = () => {
                // Ignore empty src errors (code 4) which are expected during cleanup
                if (audio.error?.code === 4) {
                  return;
                }

                console.error("Audio element error:", audio.error);
                if (!isStoppedRef.current) {
                  stop();
                  reject(new Error(`Audio error: ${audio.error?.message}`));
                }
              };

              // Set up SourceBuffer events
              sourceBuffer.onerror = () => {
                if (!isStoppedRef.current) {
                  stop();
                  reject(new Error("SourceBuffer error"));
                }
              };

              // Fetch audio stream
              const response = await (options.request?.response ??
                fetchPost(
                  "/v1/text-to-speech/",
                  {
                    text,
                    exchange_id: exchangeID,
                  },
                  {
                    signal: controller.signal,
                  },
                ));

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              if (!response.body) {
                throw new Error("Response has no body");
              }

              const reader = response.body.getReader();
              let bufferQueue: Uint8Array[] = [];
              let isProcessing = true;
              let totalBytes = 0;

              // Function to safely append buffer
              const safeAppendBuffer = (chunk: Uint8Array) => {
                if (
                  isStoppedRef.current ||
                  !sourceBuffer ||
                  sourceBuffer.updating
                ) {
                  return false;
                }

                try {
                  sourceBuffer.appendBuffer(chunk);
                  return true;
                } catch (err) {
                  // Ignore errors if we're stopping
                  if (!isStoppedRef.current) {
                    console.error("Failed to append buffer:", err);
                  }
                  return false;
                }
              };

              // Function to process queue
              const processQueue = () => {
                if (
                  isStoppedRef.current ||
                  !sourceBuffer ||
                  bufferQueue.length === 0
                ) {
                  return;
                }

                if (!sourceBuffer.updating) {
                  const chunk = bufferQueue.shift()!;
                  const appended = safeAppendBuffer(chunk);

                  if (!appended) {
                    bufferQueue.unshift(chunk);
                  }
                }
              };

              // Ends the stream once the download is over and every chunk is
              // in the buffer. Ending it earlier fails: `endOfStream` throws
              // while an append is in flight, and chunks still queued would
              // reopen the stream. Either way it is never ended, so playback
              // stalls on the last frame and `ended` - Tally's cue that it has
              // stopped talking - never fires.
              let downloadDone = false;
              const endStreamIfDrained = () => {
                if (
                  !downloadDone ||
                  isStoppedRef.current ||
                  bufferQueue.length > 0 ||
                  sourceBuffer.updating ||
                  mediaSource.readyState !== "open"
                ) {
                  return;
                }
                try {
                  mediaSource.endOfStream();
                } catch (err) {
                  console.error("Failed to end audio stream:", err);
                }
              };

              // Process stream
              const processStream = async () => {
                try {
                  while (isProcessing && !isStoppedRef.current) {
                    const { done, value } = await reader.read();

                    if (done || isStoppedRef.current) {
                      isProcessing = false;
                      downloadDone = true;
                      endStreamIfDrained();

                      if (!isStoppedRef.current) {
                        resolve();
                      }

                      break;
                    }

                    if (value && !isStoppedRef.current) {
                      totalBytes += value.byteLength;

                      // Add to queue
                      bufferQueue.push(value);

                      // Process queue
                      processQueue();
                    }
                  }
                } catch (err) {
                  // Check if it's an AbortError
                  if (err instanceof Error && err.name === "AbortError") {
                    // Stream was aborted intentionally
                    return;
                  }

                  console.error("Error processing stream:", err);
                  if (!isStoppedRef.current) {
                    stop();
                    reject(err);
                  }
                }
              };

              // Handle sourceBuffer update events
              sourceBuffer.onupdateend = () => {
                if (isStoppedRef.current) return;

                // Process next item in queue
                processQueue();
                endStreamIfDrained();

                // Start on the first appended chunk. TTS streams faster than
                // real time, so waiting for a buffer only adds silence before
                // the first word - and a short clip may never reach one.
                if (!playAttemptedRef.current && totalBytes > 0) {
                  attemptPlay(audio);
                }
              };

              // Start processing the stream
              processStream();
            } catch (err) {
              // Stopped on purpose (by the caller, or by the next utterance
              // starting): not an error, and stopping again here would cut off
              // whatever is playing now.
              if (controller.signal.aborted) return;
              console.error("Error in sourceopen handler:", err);
              if (!isStoppedRef.current) {
                stop();
                reject(err);
              }
            }
          };

          const onSourceError = () => {
            if (!isStoppedRef.current) {
              stop();
              reject(new Error("MediaSource error"));
            }
          };

          mediaSource.addEventListener("sourceopen", onSourceOpen);
          mediaSource.addEventListener("error", onSourceError);

          // Cleanup function for this speak call
          const cleanup = () => {
            mediaSource.removeEventListener("sourceopen", onSourceOpen);
            mediaSource.removeEventListener("error", onSourceError);
            URL.revokeObjectURL(objectUrl);
          };

          // Auto-cleanup after completion or error
          const originalResolve = resolve;
          const originalReject = reject;

          resolve = () => {
            cleanup();
            originalResolve();
          };

          reject = (err: any) => {
            cleanup();
            originalReject(err);
          };
        } catch (err) {
          console.error("Error in speak function:", err);
          if (!isStoppedRef.current) {
            stop();
            reject(err);
          }
        }
      });
    },
    [stop, attemptPlay],
  );

  /** Plays an already-synthesised clip on the shared element. */
  const playClip = useCallback(
    async (clip: Blob, options: SpeakOptions = {}) => {
      stop();
      const audio = acquireAudioElement();
      audioRef.current = audio;
      audio.volume = playbackVolume();
      // `stop` resets the element, which also revokes this object URL and
      // detaches this handler - so a caller's stop does not count as an end.
      audio.onended = () => {
        stop();
        options.onEnded?.();
      };
      audio.src = URL.createObjectURL(clip);
      setState("playing");
      try {
        await audio.play();
        options.onStarted?.();
      } catch (err) {
        stop();
        throw err;
      }
    },
    [stop],
  );

  return {
    speak,
    playClip,
    stop,
    state,
    isPlaying: state === "playing",
    isLoading: state === "loading",
  };
}
